import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { SoldLines } from '../types/agreements.types';
import type { ThirdPartyInvoicingContents } from '../types/thirdPartyAgreement.types';
import { unitKey, type SelectionUnit } from '../utils/invoiceSelection';
import { selectedThirdPartyLine } from '../utils/thirdPartyInvoiceLine';
import { thirdPartySoldLines } from '../utils/thirdPartyInvoicingLines';
import { useInvoiceSelection } from './useInvoiceSelection';
import type { InvoiceSelection } from './useInvoiceSelection';
import { useSelectionTableRows } from './useSelectionTableRows';
import type { SelectionTableRows } from './useSelectionTableRows';
import { useThirdPartyInvoiceSubmit, type ThirdPartyInvoiceSubmit } from './useThirdPartyInvoiceSubmit';
import { useThirdPartyInvoicingDealer } from './useThirdPartyInvoicingDealer';
import type { ThirdPartyInvoicingDealer } from './useThirdPartyInvoicingDealer';
import { useThirdPartyInvoicingQuery } from './useThirdPartyInvoicingQuery';

export interface ThirdPartyInvoicing {
    query: UseQueryResult<ThirdPartyInvoicingContents>;
    lines: SoldLines | undefined;
    selection: InvoiceSelection;
    tableRows: SelectionTableRows;
    dealer: ThirdPartyInvoicingDealer;
    invoice: ThirdPartyInvoiceSubmit;
}

const SINGLE_MODE = false;

function singleLineSelection(selection: InvoiceSelection): InvoiceSelection {
    const toggle = (unit: SelectionUnit): void => {
        const key = unitKey(unit);
        const others = selection.selection.filter((selected) => unitKey(selected) !== key);
        const ticking = others.length === selection.selection.length;
        selection.toggle(unit);
        // clearUnits writes through a state updater, so it applies after toggle's write.
        if (ticking && others.length > 0) selection.clearUnits(others);
    };

    return { ...selection, toggle };
}

export function useThirdPartyInvoicing(vrm: string, reference: string, visit: string): ThirdPartyInvoicing {
    const query = useThirdPartyInvoicingQuery(vrm, reference);
    const lines = useMemo(() => (query.data ? thirdPartySoldLines(query.data) : undefined), [query.data]);
    const selection = singleLineSelection(
        useInvoiceSelection(`${reference}:${query.dataUpdatedAt}`, lines, SINGLE_MODE),
    );
    const tableRows = useSelectionTableRows(lines, selection.selection, SINGLE_MODE, 'gross');
    const dealer = useThirdPartyInvoicingDealer(vrm, reference);

    const invoice = useThirdPartyInvoiceSubmit({
        vrm,
        reference,
        visit,
        line: selectedThirdPartyLine(query.data, selection.selection),
        dealerId: dealer.selectedId,
        onSubmitted: selection.clear,
        refetchContents: query.refetch,
    });

    return { query, lines, selection, tableRows, dealer, invoice };
}
